import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LeaseDTO, ThreadDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

/** A thread with no lease AND no unit is a direct tenant↔management conversation. */
function isDirect(t: ThreadDTO): boolean {
  return !t.leaseId && !t.unitId;
}

/** One thread's messages + a reply box (owner/staff have org-scoped access to every thread). */
function AdminThreadPanel({ threadId }: { threadId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const thread = useQuery({
    queryKey: ['admin', 'thread', threadId],
    queryFn: () => api.messaging.getThread(threadId),
  });

  // Opening a thread marks it read for this admin, so the list's unread badge clears.
  useEffect(() => {
    let cancelled = false;
    void api.messaging
      .markRead(threadId)
      .then(() => {
        if (!cancelled) void queryClient.invalidateQueries({ queryKey: ['admin', 'messages'] });
      })
      .catch(() => {
        /* non-fatal: unread badge just stays until the next load */
      });
    return () => {
      cancelled = true;
    };
  }, [threadId, queryClient]);

  const post = useMutation({
    mutationFn: () => api.messaging.postMessage(threadId, { body }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'thread', threadId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'messages'] });
    },
  });

  if (thread.isLoading) return <Loading label="Loading conversation…" />;
  if (thread.isError || !thread.data)
    return <ErrorState message={errorMessage(thread.error)} onRetry={() => thread.refetch()} />;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>
        {thread.data.thread.subject}{' '}
        {isDirect(thread.data.thread) && <span className="badge">Direct</span>}
      </h3>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
        {thread.data.messages.map((m) => (
          <li key={m.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
            <p style={{ margin: 0 }}>{m.body}</p>
            <p className="muted" style={{ margin: '0.15rem 0 0', fontSize: '0.8rem' }}>
              {formatDateTime(m.createdAt)}
            </p>
          </li>
        ))}
      </ol>
      <form
        className="stack"
        style={{ marginTop: '1rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) post.mutate();
        }}
      >
        {post.isError && <StatusMessage tone="err">{errorMessage(post.error)}</StatusMessage>}
        <div>
          <label htmlFor="admin-reply-body">Your reply</label>
          <textarea
            id="admin-reply-body"
            rows={3}
            value={body}
            maxLength={8000}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div>
          <button type="submit" disabled={post.isPending || !body.trim()}>
            {post.isPending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** A lease option's readable label — "Main House — John Sauro, Tyren Clark". */
function leaseLabel(l: LeaseDTO): string {
  const who = l.tenants
    .map((lt) => (lt.tenant ? `${lt.tenant.firstName} ${lt.tenant.lastName}` : ''))
    .filter(Boolean)
    .join(', ');
  const unit = l.unitLabel ?? l.unitId;
  return who ? `${unit} — ${who}` : unit;
}

/**
 * Start a NEW conversation as the owner/staff. A thread is opened on a chosen LEASE — the server
 * derives its participants (that lease's tenants + the administering owner/staff), so the message
 * reaches every tenant on the lease. (Owner/staff must target a lease/unit; only a tenant may open
 * a lease-less "direct" thread — so this composer is lease-scoped by design.)
 */
function NewAdminThreadForm({ onCreated }: { onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const leasesQuery = useQuery({ queryKey: ['admin', 'leases'], queryFn: () => api.leases.list() });
  const [leaseId, setLeaseId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.messaging.createThread({ leaseId, subject: subject.trim(), body: body.trim() }),
    onSuccess: (res) => {
      setLeaseId('');
      setSubject('');
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'messages'] });
      onCreated(res.thread.id);
    },
  });

  const leases = leasesQuery.data?.leases ?? [];

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (leaseId && subject.trim() && body.trim()) create.mutate();
      }}
    >
      <h3 style={{ marginTop: 0 }}>New message</h3>
      {create.isError && <StatusMessage tone="err">{errorMessage(create.error)}</StatusMessage>}
      <div>
        <label htmlFor="admin-new-lease">Send to (lease)</label>
        <select
          id="admin-new-lease"
          value={leaseId}
          disabled={leasesQuery.isLoading}
          onChange={(e) => setLeaseId(e.target.value)}
        >
          <option value="">{leasesQuery.isLoading ? 'Loading leases…' : 'Select a lease…'}</option>
          {leases.map((l) => (
            <option key={l.id} value={l.id}>
              {leaseLabel(l)}
            </option>
          ))}
        </select>
        <p className="field-hint">Everyone on this lease will receive the message in their portal.</p>
      </div>
      <div>
        <label htmlFor="admin-new-subject">Subject</label>
        <input
          id="admin-new-subject"
          type="text"
          value={subject}
          maxLength={200}
          placeholder="What's this about?"
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="admin-new-body">Message</label>
        <textarea
          id="admin-new-body"
          rows={4}
          value={body}
          maxLength={8000}
          placeholder="Write your message… (it stays private, inside the portal)"
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <div>
        <button
          type="submit"
          disabled={create.isPending || !leaseId || !subject.trim() || !body.trim()}
        >
          {create.isPending ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
}

export function AdminMessages() {
  const query = useQuery({
    queryKey: ['admin', 'messages'],
    queryFn: () => api.messaging.listThreads(),
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const onCreated = (id: string) => {
    setSelected(id);
    setComposing(false);
  };

  if (query.isLoading) return <Loading label="Loading messages…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const threads = query.data.threads;
  const active = selected ?? threads[0]?.id ?? null;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1 style={{ marginBottom: 0 }}>Messages</h1>
        <button
          type="button"
          className={composing ? 'secondary' : ''}
          aria-expanded={composing}
          onClick={() => setComposing((v) => !v)}
        >
          {composing ? 'Cancel' : 'New message'}
        </button>
      </div>
      <p className="subtitle">Conversations with your tenants across the portfolio.</p>

      {composing && (
        <div style={{ marginBottom: '1rem' }}>
          <NewAdminThreadForm onCreated={onCreated} />
        </div>
      )}

      {threads.length === 0 ? (
        !composing && (
          <p className="muted">
            No conversations yet. Choose <strong>New message</strong> to write to a lease, or a
            tenant can start one from their portal.
          </p>
        )
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'minmax(220px, 1fr) 2fr' }}>
          <nav aria-label="Conversations">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={active === t.id ? '' : 'secondary'}
                    style={{ width: '100%', textAlign: 'left' }}
                    aria-current={active === t.id ? 'true' : undefined}
                    onClick={() => setSelected(t.id)}
                  >
                    {t.subject}
                    {isDirect(t) ? ' · Direct' : ''}
                    {t.unreadCount ? ` (${t.unreadCount} unread)` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <section aria-labelledby="admin-conversation-heading">
            <h2 id="admin-conversation-heading" className="visually-hidden">
              Conversation
            </h2>
            {active && <AdminThreadPanel threadId={active} />}
          </section>
        </div>
      )}
    </>
  );
}
